// Endpoints RTK Query da feature stores. Tipos manuais — quando openapi-typescript
// regenerar generated.ts, migrar pra paths['/stores']['get']['responses']['200'].
// FIXME: aguardar openapi-typescript real.
import { api } from '@/lib/api';
import type { AvatarTone } from '@/components/ui/Avatar';
import type { Role } from '@/config/constants';
import type { PeriodRange, ResolvedPeriod } from '@/features/management';
import type { MetricsDelta } from '@/features/dashboard';

// Hex de cor da loja. Tipo aberto (`#${string}`) pra aceitar tanto a paleta nova
// quanto lojas legadas salvas com hex antigo — evita mismatch de tipo em runtime.
// A paleta selecionável vive em CreateStorePage (COLORS).
export type StoreColor = `#${string}`;

export type WeekDayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface BusinessHours {
  days: Record<WeekDayKey, { enabled: boolean; open: string; close: string }>;
}

/**
 * RF-01/02 — intervalo de agrupamento das mensagens do cliente antes de acionar a
 * IA, e o ÚNICO controle de ritmo que a tela expõe (o atraso de leitura/digitação
 * virou padrão fixo no backend). `delayMs` é a janela deslizante, que reinicia a
 * cada mensagem; `maxWaitMs` é o teto absoluto contado da primeira do lote e precisa
 * ser maior que a janela. null/ausente = default do backend (2 a 10 min).
 */
export interface AiGroupingDelay {
  enabled?: boolean;
  delayMs?: number;
  maxWaitMs?: number;
}

/**
 * RF-01 — nível de atuação da IA na loja, em ordem PROGRESSIVA (cada um inclui o
 * anterior). `COMPLETO` existe no contrato mas o back devolve 422
 * AI_LEVEL_UNAVAILABLE: o gateway de pagamento ainda não foi definido, então a UI
 * mostra o card desabilitado.
 */
export type AiAutonomyLevel = 'TRIAGEM' | 'ORCAMENTO' | 'COMPLETO';

export interface Store {
  id: string;
  name: string;
  city: string;
  color: StoreColor;
  whatsappNumber: string | null;
  whatsappOnline: boolean;
  /** ISO date — última sincronia */
  whatsappSyncedAt: string | null;
  /** Há um token de envio salvo (Embedded Signup ou token manual). */
  whatsappConnected: boolean;
  /** Número/nome exibível do número conectado (quando a Meta retornou). */
  whatsappDisplayNumber: string | null;
  /** ISO date — quando o token/conexão foi gravado pela última vez. */
  whatsappConnectedAt: string | null;
  /** Token salvo mas rejeitado pela Meta no último envio (code 190) → renovar. */
  whatsappTokenInvalid: boolean;
  /** Id do canal NotificaMe conectado (null = não conectado). Token NUNCA sai daqui. */
  notificameChannelId: string | null;
  /** ISO date — quando a conexão NotificaMe foi gravada. */
  notificameConnectedAt: string | null;
  aiTone: 'FORMAL' | 'CALOROSO' | 'DIRETO';
  /** RF-01 — até onde a IA vai sozinha nesta loja. */
  aiAutonomyLevel: AiAutonomyLevel;
  aiUsesLocalTable: boolean;
  aiHandoffAfterQuote: boolean;
  /** Lembrete automático ao cliente quando o orçamento fica sem resposta. */
  quoteReminderEnabled: boolean;
  /** Texto do lembrete (null = usa o default do sistema). */
  quoteReminderMessage: string | null;
  /** Minutos até o lembrete de orçamento (null = default do sistema, 30 min). */
  quoteReminderMinutes: number | null;
  /** Pesquisa de satisfação: pergunta enviada ao concluir (null = texto padrão). */
  surveyMessage: string | null;
  /** Agradecimento depois da nota, com o placeholder `{link}` (null = texto padrão). */
  surveyThanksMessage: string | null;
  /** Link de avaliação da loja no Google (https); null = sem pedido de avaliação. */
  googleReviewUrl: string | null;
  /** RF-03 — saudação da IA; null = default do sistema. Placeholder `{nome}`. */
  aiGreetingTemplate: string | null;
  /** RF-03 — reconhecimento da categoria; null = default. `{nome}` `{categoria}` `{beneficios}`. */
  aiTierRecognitionTemplate: string | null;
  /** Instruções de COMO a IA fala nesta loja; null = só o prompt base do sistema. */
  aiStyleInstructions: string | null;
  businessHours: BusinessHours | null;
  /** RF-01/02 — intervalo de agrupamento (null = defaults do sistema, 2 a 10 min). */
  aiGroupingDelay: AiGroupingDelay | null;
  /** Chave-mestra da resposta automática da IA nesta loja. Desligada, o inbound vai
   *  direto pra Fila. NÃO desliga transcrição de áudio nem análise de conversas. */
  aiAutoReplyEnabled: boolean;
  /** RF-03 — lembrete por inatividade ligado nesta loja. */
  inactivityReminderEnabled: boolean;
  /** Minutos de silêncio até o lembrete (null = default do sistema, 25 min). */
  inactivityReminderMinutes: number | null;
  /** KPIs do mês */
  monthlyRevenueCents: number;
  activeChats: number;
  vendorsOnline: number;
  productCount: number;
  vendorCount: number;
  /** Optimistic locking — readme §19. */
  version: number;
  createdAt: string;
}

export interface StoreMember {
  userId: string;
  name: string;
  email: string;
  role: Role;
  avatarTone: AvatarTone;
  /** URL da foto de perfil (já com token). null quando o membro não tem foto. */
  avatarUrl?: string | null;
  online: boolean;
  /** Acesso ativo. `false` = soft-deleted; só vem na lista quando includeInactive. */
  active: boolean;
  rating: number;
  salesCount: number;
  doneCount: number;
}

export interface StorePrice {
  id: string;
  productName: string;
  sku: string;
  variation: string;
  quantity: number;
  leadTimeDays: number;
  priceCents: number;
}

export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface CreateStoreInput {
  name: string;
  city: string;
  color: StoreColor;
  phone?: string;
  priceSource: 'COPY' | 'IMPORT' | 'EMPTY';
  copyFromStoreId?: string;
  members?: { email: string; role: Exclude<Role, 'GESTOR'> }[];
}

export interface CreateStoreResponse extends Store {
  pendingInvites: number;
  copiedPrices: number;
}

export interface UpdateStoreInput {
  id: string;
  /** Optimistic locking — readme §19. */
  version?: number;
  name?: string;
  city?: string;
  color?: StoreColor;
  active?: boolean;
  whatsappNumber?: string;
  businessHours?: BusinessHours;
  aiTone?: Store['aiTone'];
  /** RF-01 — nível de atuação da IA. `COMPLETO` é recusado com 422 pelo back. */
  aiAutonomyLevel?: AiAutonomyLevel;
  aiUsesLocalTable?: boolean;
  aiHandoffAfterQuote?: boolean;
  quoteReminderEnabled?: boolean;
  quoteReminderMessage?: string | null;
  /** Pesquisa de satisfação; string vazia vira null no back (texto padrão / sem link). */
  surveyMessage?: string | null;
  surveyThanksMessage?: string | null;
  googleReviewUrl?: string | null;
  /** RF-03 — textos da IA; string vazia vira null no back (volta ao default). */
  aiGreetingTemplate?: string | null;
  aiTierRecognitionTemplate?: string | null;
  aiStyleInstructions?: string | null;
  aiGroupingDelay?: AiGroupingDelay | null;
  aiAutoReplyEnabled?: boolean;
  inactivityReminderEnabled?: boolean;
  /** Tempos dos lembretes em minutos; null volta ao default do sistema (25 e 30). */
  inactivityReminderMinutes?: number | null;
  quoteReminderMinutes?: number | null;
}

/** Status de convite que a lista da loja devolve — só os "em aberto". */
export type InviteStatus = 'PENDENTE' | 'EXPIRADO';

export interface StoreInvite {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  /** EXPIRADO continua na lista (TTL de 2h): o card oferece gerar link novo. */
  status: InviteStatus;
  createdAt: string;
  expiresAt: string;
}

export interface ResendInviteResponse {
  url: string;
}

export interface AddMemberInput {
  storeId: string;
  email: string;
  role: Exclude<Role, 'GESTOR'>;
}

/** PATCH /v1/users/:id — edição de perfil de membro pelo GESTOR (readme §22). */
export interface UpdateMemberInput {
  userId: string;
  name?: string;
  role?: Exclude<Role, 'GESTOR'>;
  active?: boolean;
}

export interface UpsertPriceInput {
  storeId: string;
  priceId?: string;
  productName: string;
  sku: string;
  variation: string;
  quantity: number;
  leadTimeDays: number;
  priceCents: number;
}

/** Modelo de preço (espelha o enum `StorePriceModel` do back). */
export type StorePriceModel = 'TIER' | 'AREA' | 'LINEAR' | 'FLAT';

/**
 * Linha do importador em lote. Carrega os metadados ricos da reconstrução do PDF
 * (modelo + bandas) pra back persistir e a Fase 2 compilar em `Product.pricingConfig`.
 */
export interface BatchPriceRow {
  productName: string;
  sku?: string;
  variation?: string;
  category?: string;
  priceModel?: StorePriceModel;
  quantity?: number | null;
  minQty?: number | null;
  maxQty?: number | null;
  minAreaM2?: number | null;
  maxAreaM2?: number | null;
  leadTimeDays?: number;
  priceCents: number;
}

export interface BatchUpsertPricesInput {
  storeId: string;
  rows: BatchPriceRow[];
  importBatchId?: string;
}

export interface BatchUpsertPricesResult {
  importBatchId: string;
  count: number;
}

/** RF-05: custo de IA por tarefa no escopo/período (ex.: 'reply', 'analysis'). */
export interface LlmCostTaskBreakdown {
  task: string;
  calls: number;
  costUsd: number;
}

/**
 * RF-05: custo de IA consolidado de Empresas — envelope de período padrão +
 * totalizador em USD. Valores em dólares (não centavos): nunca misturar com BRL.
 */
export interface LlmCostDto {
  period: ResolvedPeriod;
  from: string;
  to: string;
  totalUsd: number;
  calls: number;
  /** Conversas distintas com ao menos um custo registrado no período. */
  conversationsWithCost: number;
  avgCostPerConversationUsd: number;
  byTask: LlmCostTaskBreakdown[];
  deltas: { totalUsd: MetricsDelta };
}

export interface LlmCostArgs extends PeriodRange {
  storeId?: string;
}

/** Monta a query string ignorando parâmetros indefinidos (espelha management/api.ts). */
function llmCostQs(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== '') as [string, string][];
  return entries.length ? `?${new URLSearchParams(entries).toString()}` : '';
}

/**
 * Um número de WhatsApp vinculado à loja. **N por loja** — cada um é uma linha por
 * onde o cliente fala com a empresa, e exatamente um é o principal.
 *
 * O token de envio NUNCA vem no payload, nem cifrado: a tela só precisa saber que
 * ele existe e está válido, e `active`/`tokenInvalid` já dizem isso.
 *
 * `qualityRating` é a nota da Meta (GREEN/YELLOW/RED) — uma PREVISÃO de que o número
 * vai continuar entregando, não um status atual. RED antecede restrição.
 */
export interface StoreWhatsappNumber {
  id: string;
  phoneNumberId: string;
  wabaId: string | null;
  displayNumber: string | null;
  verifiedName: string | null;
  qualityRating: string | null;
  isDefault: boolean;
  active: boolean;
  tokenInvalid: boolean;
  connectedAt: string;
}

/**
 * Estado da importação de histórico de Coexistence de um número.
 *
 * 🔴 **Não há percentual, e isso é da Meta, não da implementação.** Ela não informa
 * quantas mensagens virão, não manda evento de "acabou" e não tem endpoint de status.
 * Qualquer `%` seria inventado — e uma barra travada em 40% gera mais suporte que um
 * contador honesto subindo.
 */
export interface HistoryImportState {
  /** null = nunca foi pedida uma importação para este número. */
  id: string | null;
  status: 'REQUESTED' | 'RECEIVING' | 'COMPLETED' | 'PARTIAL' | 'FAILED' | null;
  requestedAt: string | null;
  finishedAt: string | null;
  lastEventAt: string | null;
  /** Mensagem já traduzida pelo back para algo acionável. */
  error: string | null;
  /** Jobs que morreram. > 0 → a importação não fecha sozinha; cabe "Retomar". */
  failedCount: number;
  conversations: number;
  messages: number;
  /**
   * ⚠️ Não existe percentual aqui, e é decisão de produto — não limitação.
   *
   * O indicador é indeterminado e roda até a importação terminar de verdade; quem diz o
   * quanto já entrou é o contador. Percentual num processo entregue em lotes fora de
   * ordem produz "por que travou em 40%?", e a resposta honesta ("não travou, o lote
   * seguinte demorou") não cabe numa barra. O back até LÊ o número da Meta, mas para
   * detectar o fim real, não para exibir.
   */
  /** ⚠️ true = a IA está PAUSADA nesta loja até a importação terminar. */
  running: boolean;
  /**
   * O gateway confirmou que a Meta entregou as TRÊS fases do despejo?
   *
   * ⚠️ Três estados, não dois. `null` = não sabemos (gateway mudo ou importação anterior
   * ao campo) e a tela não pode transformar isso em aviso — dizer "veio incompleto"
   * porque o túnel caiu seria pior que não dizer nada.
   */
  historyComplete: boolean | null;
  /** Fases que chegaram. `[0]` num despejo de 3 é o caso de 27/08. */
  phasesRecebidas: number[];
}

export const storesApi = api.injectEndpoints({
  endpoints: (build) => ({
    // RF-05: custo de IA consolidado da rede (role ≥ GERENTE no back),
    // respeitando o filtro loja/período da página Empresas.
    getLlmCost: build.query<LlmCostDto, LlmCostArgs>({
      query: ({ period, from, to, storeId }) =>
        `/analytics/custo-ia${llmCostQs({ period, from, to, storeId })}`,
    }),
    listStores: build.query<CursorPage<Store>, { cursor?: string } | void>({
      query: (params) =>
        params?.cursor ? `/stores?cursor=${encodeURIComponent(params.cursor)}` : '/stores',
      // Back devolve { data, pageInfo: { nextCursor, hasMore } } (envelope canônico
      // do readme §12). Front consome shape { items, nextCursor } — sem isso a
      // lista vira undefined silenciosamente em StoresPage/CreateStorePage.
      transformResponse: (raw: {
        data: Store[];
        pageInfo: { nextCursor: string | null; hasMore: boolean };
      }): CursorPage<Store> => ({
        items: raw.data,
        nextCursor: raw.pageInfo.nextCursor,
      }),
      providesTags: (result) =>
        result
          ? [
              { type: 'Store' as const, id: 'LIST' },
              ...result.items.map((s) => ({ type: 'Store' as const, id: s.id })),
            ]
          : [{ type: 'Store' as const, id: 'LIST' }],
    }),
    getStore: build.query<Store, string>({
      query: (id) => `/stores/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Store' as const, id }],
    }),
    listStoreMembers: build.query<StoreMember[], { storeId: string; includeInactive?: boolean }>({
      // includeInactive só é honrado pelo back p/ role ≥ GERENTE; os
      // consumidores da loja/inbox omitem o flag e seguem recebendo só ativos.
      query: ({ storeId, includeInactive }) =>
        `/stores/${storeId}/members${includeInactive ? '?includeInactive=true' : ''}`,
      providesTags: (_r, _e, { storeId }) => [{ type: 'StoreMember' as const, id: storeId }],
    }),
    listStorePrices: build.query<StorePrice[], string>({
      query: (storeId) => `/stores/${storeId}/prices`,
      providesTags: (_r, _e, id) => [{ type: 'StorePrice' as const, id }],
    }),
    createStore: build.mutation<CreateStoreResponse, CreateStoreInput>({
      query: (body) => ({ url: '/stores', method: 'POST', body }),
      invalidatesTags: [{ type: 'Store', id: 'LIST' }],
    }),
    updateStore: build.mutation<Store, UpdateStoreInput>({
      query: ({ id, ...patch }) => ({ url: `/stores/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Store' as const, id: arg.id },
        { type: 'Store' as const, id: 'LIST' },
      ],
    }),
    deleteStore: build.mutation<{ ok: true }, string>({
      query: (id) => ({ url: `/stores/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Store' as const, id },
        { type: 'Store' as const, id: 'LIST' },
      ],
    }),
    addMember: build.mutation<StoreMember, AddMemberInput>({
      query: ({ storeId, ...body }) => ({
        url: `/stores/${storeId}/members`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'StoreMember' as const, id: arg.storeId }],
    }),
    removeMember: build.mutation<{ ok: true }, { storeId: string; userId: string }>({
      query: ({ storeId, userId }) => ({
        url: `/stores/${storeId}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'StoreMember' as const, id: arg.storeId }],
    }),
    listStoreInvites: build.query<StoreInvite[], string>({
      // Só convites PENDENTE não-vencidos (back filtra).
      query: (storeId) => `/stores/${storeId}/invites`,
      providesTags: (_r, _e, id) => [{ type: 'StoreInvite' as const, id }],
    }),
    resendInvite: build.mutation<ResendInviteResponse, { storeId: string; inviteId: string }>({
      query: ({ storeId, inviteId }) => ({
        url: `/stores/${storeId}/invites/${inviteId}/resend`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'StoreInvite' as const, id: arg.storeId }],
    }),
    upsertPrice: build.mutation<StorePrice, UpsertPriceInput>({
      query: ({ storeId, priceId, ...body }) => ({
        url: priceId ? `/stores/${storeId}/prices/${priceId}` : `/stores/${storeId}/prices`,
        method: priceId ? 'PATCH' : 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'StorePrice' as const, id: arg.storeId }],
    }),
    batchUpsertPrices: build.mutation<BatchUpsertPricesResult, BatchUpsertPricesInput>({
      query: ({ storeId, rows, importBatchId }) => ({
        url: `/stores/${storeId}/prices/batch`,
        method: 'POST',
        body: { rows, ...(importBatchId ? { importBatchId } : {}) },
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'StorePrice' as const, id: arg.storeId }],
    }),
    deletePrice: build.mutation<{ ok: true }, { storeId: string; priceId: string }>({
      query: ({ storeId, priceId }) => ({
        url: `/stores/${storeId}/prices/${priceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'StorePrice' as const, id: arg.storeId }],
    }),
    // Endpoints de /users consumidos pela UI de equipe — vivem aqui pra
    // invalidar as tags StoreMember (membro pode estar em N lojas; tag sem id
    // invalida o tipo inteiro).
    updateMember: build.mutation<unknown, UpdateMemberInput>({
      query: ({ userId, ...body }) => ({
        url: `/users/${userId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['StoreMember'],
    }),
    // PUT /stores/members/:userId/stores — vincula o conjunto de lojas a um
    // membro (usado pra GERENTE, que gerencia N lojas). Contrato
    // fixo do backend. Invalida StoreMember (membro pode estar em N lojas; tag
    // sem id invalida o tipo inteiro).
    setMemberStores: build.mutation<unknown, { userId: string; storeIds: string[] }>({
      query: ({ userId, storeIds }) => ({
        url: `/stores/members/${userId}/stores`,
        method: 'PUT',
        body: { storeIds },
      }),
      invalidatesTags: ['StoreMember'],
    }),
    generateResetLink: build.mutation<{ url: string }, string>({
      query: (userId) => ({
        url: `/users/${userId}/password-reset-link`,
        method: 'POST',
      }),
    }),
    // Sem productName apaga a tabela inteira da loja; com, só aquele material.
    deleteAllPrices: build.mutation<
      { ok: true; deleted: number },
      { storeId: string; productName?: string }
    >({
      query: ({ storeId, productName }) => ({
        url: `/stores/${storeId}/prices`,
        method: 'DELETE',
        ...(productName ? { params: { productName } } : {}),
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'StorePrice' as const, id: arg.storeId }],
    }),
    // Importa (copia) a tabela de preços de outra loja da mesma empresa pra esta loja.
    importPricesFromStore: build.mutation<
      { ok: true; importBatchId: string; count: number },
      { storeId: string; sourceStoreId: string; productNames?: string[]; replace?: boolean }
    >({
      query: ({ storeId, ...body }) => ({
        url: `/stores/${storeId}/prices/import`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'StorePrice' as const, id: arg.storeId }],
    }),
    // Envio de mensagem de teste WhatsApp (template hello_world por padrão) —
    // evidência p/ o App Review da Meta. `storeId` faz o envio sair pelo número
    // conectado da loja (Embedded Signup). Não invalida cache (fire-and-forget).
    sendWhatsappTest: build.mutation<
      { wamid: string; provider: string; to: string },
      { to: string; templateName?: string; language?: string; storeId?: string }
    >({
      query: (body) => ({ url: '/whatsapp/test-send', method: 'POST', body }),
    }),
    // Embedded Signup (Facebook Login for Business): troca o `code` do FB.login por
    // token, inscreve a WABA e registra o número no backend. Invalida a Store p/ a
    // UI refletir a conexão (display number / status).
    connectWhatsapp: build.mutation<
      {
        ok: true;
        storeId: string;
        phoneNumberId: string;
        wabaId: string;
        displayNumber: string | null;
        verifiedName: string | null;
      },
      {
        code: string;
        /** Ausente no fluxo de Coexistence — o backend descobre o número pela WABA. */
        phoneNumberId?: string;
        wabaId: string;
        /** Portfólio da Meta; só diagnóstico, não é persistido. */
        businessId?: string;
        storeId: string;
      }
    >({
      query: (body) => ({ url: '/whatsapp-onboarding/connect', method: 'POST', body }),
      // Conectar ADICIONA um número à loja (N por loja) — sem invalidar a lista, o
      // número recém-vinculado só apareceria no próximo carregamento da aba.
      invalidatesTags: (_r, _e, arg) => [
        { type: 'StoreWhatsappNumber' as const, id: arg.storeId },
        { type: 'Store' as const, id: arg.storeId },
        { type: 'Store' as const, id: 'LIST' },
      ],
    }),
    // Atualização manual do token de envio (modo de teste — o token da Meta expira
    // com frequência e o número de teste não passa por Embedded Signup). Cifra e
    // grava o token na loja; invalida a Store p/ refletir display/status.
    setWhatsappToken: build.mutation<
      {
        ok: true;
        storeId: string;
        phoneNumberId: string;
        displayNumber: string | null;
        subscribed?: boolean;
      },
      { storeId: string; accessToken: string; phoneNumberId?: string; wabaId?: string }
    >({
      query: (body) => ({ url: '/whatsapp-onboarding/token', method: 'POST', body }),
      // Também pode criar um número (quando vem com phoneNumberId) ou reativar o
      // token de um existente — nos dois casos a lista muda.
      invalidatesTags: (_r, _e, arg) => [
        { type: 'StoreWhatsappNumber' as const, id: arg.storeId },
        { type: 'Store' as const, id: arg.storeId },
        { type: 'Store' as const, id: 'LIST' },
      ],
    }),
    // Conecta o canal NotificaMe Hub da loja (substitui o Embedded Signup da Meta
    // na v2). Cifra o X-Api-Token no back e tenta auto-inscrever o webhook — daí o
    // `webhookSubscribed` best-effort na resposta. Token é write-only: nunca volta.
    // Invalida a Store p/ refletir notificameChannelId/notificameConnectedAt.
    connectNotificame: build.mutation<
      { connected: true; webhookSubscribed: boolean; webhookError?: string },
      { storeId: string; channelId: string; apiToken: string; channelToken?: string }
    >({
      query: ({ storeId, ...body }) => ({
        url: `/stores/${storeId}/notificame`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Store' as const, id: arg.storeId },
        { type: 'Store' as const, id: 'LIST' },
      ],
    }),
    // Desconecta o canal NotificaMe (limpa as credenciais no back; histórico fica).
    disconnectNotificame: build.mutation<{ connected: false }, string>({
      query: (storeId) => ({ url: `/stores/${storeId}/notificame`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, storeId) => [
        { type: 'Store' as const, id: storeId },
        { type: 'Store' as const, id: 'LIST' },
      ],
    }),

    // --- Números de WhatsApp da loja (N por loja) ---------------------------------
    // A lista inclui os desconectados (`active: false`): as conversas que entraram
    // por eles continuam apontando para a linha, então o número não some do histórico.
    listWhatsappNumbers: build.query<StoreWhatsappNumber[], string>({
      query: (storeId) => ({ url: `/stores/${storeId}/whatsapp-numbers` }),
      providesTags: (_r, _e, storeId) => [{ type: 'StoreWhatsappNumber' as const, id: storeId }],
    }),
    // Troca o número principal. Invalida `Store` junto porque o back faz dual-write:
    // as colunas `whatsapp*` da loja espelham o principal enquanto a transição durar.
    setDefaultWhatsappNumber: build.mutation<
      StoreWhatsappNumber,
      { storeId: string; numberId: string }
    >({
      query: ({ storeId, numberId }) => ({
        url: `/stores/${storeId}/whatsapp-numbers/${numberId}/default`,
        method: 'PATCH',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'StoreWhatsappNumber' as const, id: arg.storeId },
        { type: 'Store' as const, id: arg.storeId },
        { type: 'Store' as const, id: 'LIST' },
      ],
    }),
    // Desconecta. O back recusa (409 DEFAULT_NUMBER) desconectar o principal havendo
    // outros ativos — a loja ficaria sem saída definida para disparos.
    disconnectWhatsappNumber: build.mutation<
      StoreWhatsappNumber,
      { storeId: string; numberId: string }
    >({
      query: ({ storeId, numberId }) => ({
        url: `/stores/${storeId}/whatsapp-numbers/${numberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'StoreWhatsappNumber' as const, id: arg.storeId },
        { type: 'Store' as const, id: arg.storeId },
        { type: 'Store' as const, id: 'LIST' },
      ],
    }),

    // --- Importação de histórico de Coexistence -----------------------------------
    // ⚠️ Enquanto `running` é true a IA fica PAUSADA na loja inteira — não é detalhe
    // técnico, é efeito visível no atendimento, e a tela precisa dizer isso.
    getHistoryImport: build.query<HistoryImportState, { storeId: string; numberId: string }>({
      query: ({ storeId, numberId }) => ({
        url: `/stores/${storeId}/whatsapp-numbers/${numberId}/import-history`,
      }),
      providesTags: (_r, _e, arg) => [{ type: 'HistoryImport' as const, id: arg.numberId }],
    }),
    // O POST confirma o PEDIDO, não a importação: as mensagens chegam depois, em lotes,
    // pelos webhooks. Quem mostra progresso é o GET acima + o evento de WebSocket.
    startHistoryImport: build.mutation<HistoryImportState, { storeId: string; numberId: string }>({
      query: ({ storeId, numberId }) => ({
        url: `/stores/${storeId}/whatsapp-numbers/${numberId}/import-history`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'HistoryImport' as const, id: arg.numberId }],
    }),
    // Retoma de onde parou (replay dos jobs mortos). Seguro por construção: o back
    // deduplica por `wamid`, então reprocessar não duplica mensagem.
    resumeHistoryImport: build.mutation<HistoryImportState, { storeId: string; numberId: string }>({
      query: ({ storeId, numberId }) => ({
        url: `/stores/${storeId}/whatsapp-numbers/${numberId}/import-history/resume`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'HistoryImport' as const, id: arg.numberId }],
    }),
    // Encerra à mão e DESTRAVA a IA. Existe porque a trava não tem teto de tempo: sem
    // esta saída, uma importação que nunca fecha deixaria a loja muda indefinidamente.
    finishHistoryImport: build.mutation<HistoryImportState, { storeId: string; numberId: string }>({
      query: ({ storeId, numberId }) => ({
        url: `/stores/${storeId}/whatsapp-numbers/${numberId}/import-history/finish`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'HistoryImport' as const, id: arg.numberId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetHistoryImportQuery,
  useStartHistoryImportMutation,
  useResumeHistoryImportMutation,
  useFinishHistoryImportMutation,
  useListStoresQuery,
  useGetStoreQuery,
  useListStoreMembersQuery,
  useListStorePricesQuery,
  useCreateStoreMutation,
  useUpdateStoreMutation,
  useDeleteStoreMutation,
  useAddMemberMutation,
  useRemoveMemberMutation,
  useListStoreInvitesQuery,
  useResendInviteMutation,
  useUpsertPriceMutation,
  useBatchUpsertPricesMutation,
  useDeletePriceMutation,
  useDeleteAllPricesMutation,
  useImportPricesFromStoreMutation,
  useUpdateMemberMutation,
  useSetMemberStoresMutation,
  useGenerateResetLinkMutation,
  useSendWhatsappTestMutation,
  useConnectWhatsappMutation,
  useSetWhatsappTokenMutation,
  useConnectNotificameMutation,
  useDisconnectNotificameMutation,
  useListWhatsappNumbersQuery,
  useSetDefaultWhatsappNumberMutation,
  useDisconnectWhatsappNumberMutation,
  useGetLlmCostQuery,
} = storesApi;
